/** @module WaterfallChartComponent */
import AxisHelper from '../chart_helpers/AxisHelper'
import { getPatternByIndex } from '../utils/patternsUtils'
import WaterfallChartViz from '../vizs/WaterfallChartViz'
import BaseComponent from './BaseComponent'

/**
 * {-EN-}
 * # {componentLabel} (`class {componentClassName}`)
 * A waterfall chart illustrates cumulative positive and negative changes on a variable using floating columns. It shows how different components contribute to the overall change, helping to analyze and understand the flow and significance of factors over time or across categories.
 *
 * ### Example Usage
 * ```javascript
 *   import {WaterfallChart} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new WaterfallChart($el, data, config);
 *
 * ```
 *
 *
 * ### Data structure {.section-data-structure}
 * The tabular data for this chart should be a table with 2 columns. Each row represents a bar, and the first column should be the id/label of the bar, the second column its value.
 *
 * You can use the attibute ```computedTotal``` to add the total column at the end.
 *
 * |   id     | value  |
 * | -------- | ------ |
 * |   a      |    22  |
 * |   b      |    11  |
 * |   c      |    3   |
 * |   d      |    30  |
 *
 * {.data-table}
 *
 * {-ES-}
 * # {componentLabelEs} (`class {componentClassName}`)
 * Un gráfico de cascada ilustra los cambios positivos y negativos acumulados en una variable mediante columnas flotantes. Muestra cómo los diferentes componentes contribuyen al cambio general, ayudando a analizar y comprender el flujo y la importancia de los factores a lo largo del tiempo o entre categorías.
 *
 * ### Ejemplo de uso
 * ```javascript
 *   import {WaterfallChart} from 'sdv-lib'
 *
 *   // Instantiate the chart with a DOM element, a data Array and a config Object
 *   let myChart = new WaterfallChart($el, data, config);
 *
 * ```
 *
 *
 * ### Estructura de datos {.section-data-structure}
 * Los datos tabulares de este gráfico deben ser una tabla con 2 columnas. Cada fila representa una barra, y la primera columna debe ser la identificación/etiqueta de la barra, la segunda columna su valor.
 *
 * Puedes usar el atributo ```computedTotal``` para agregar la columna total al final.
 *
 * |   id     | valor  |
 * | -------- | ------ |
 * |   a      |    22  |
 * |   b      |    11  |
 * |   c      |    3   |
 * |   d      |    30  |
 *
 * {.data-table}
 *
 */
export default class WaterfallChartComponent extends BaseComponent {
  static componentLabel = 'Waterfall chart'
  static componentLabelEs = 'Gráfico de cascada'
  static vizClass = WaterfallChartViz
  static dataFormat = [
    { index: 0, axis: 'x0', types: [String], role: 'id', roleLabel: 'Category' },
    { index: 1, axis: 'y0', types: [Number], role: 'value', roleLabel: 'Value' },
    // {index:2,axis:'none',types:[String, null],role:'type',roleLabel:'Type'}
  ]
  constructor($root, config, data) {
    super($root, config, data)
  }
  static defaultConfig = {
    title: this.componentLabel,
    AxisHelper: {
      x: {
        /**
         * {-EN-} Show/hide labels for each bar on the X axis.
         * {-ES-} Mostrar/ocultar etiquetas para cada barra en el eje X.
         */
        showTickValues: {
          default: false,
        },
      },
    },
    TooltipBehavior: {
      axis: {
        y: {
          tooltipValueFormat: {
            exposed: true,
            nonEditable: false,
          },
        },
      },
    },
  }

  static dataRequirements = {
    sign: 'both',
    requirementsStrength: 30,
  }

  static accessibilityRules = {
    ...super.accessibilityRules,
    textures: {
      ...super.accessibilityRules.textures,
      check: (config, data) => {
        if (!config.hasColorPatterns) return true
        if (config.ItemsHelper.colorDict) {
          const colorKey =
            typeof config.ItemsHelper.colorKey === 'function' ? 'id' : config.ItemsHelper.colorKey
          const groupedDataKeys = new Set(data.map(d => d[colorKey]))
          let totalPatterns = [...groupedDataKeys].reduce((accumulator, current) => {
            accumulator += config.ItemsHelper.colorDict[current]?.pattern?.id ? 1 : 0
            return accumulator
          }, 0)
          return totalPatterns >= groupedDataKeys.size - 1
        }
        return false
      },
      applyAutoConfigChange: function (config, data, colors, getColor) {
        if (this.check(config, data)) return {}
        const oldConfig = {
          ItemsHelper: {
            ...config.ItemsHelper,
            colorDict: config.ItemsHelper.colorDict
              ? {
                  ...config.ItemsHelper.colorDict,
                }
              : undefined,
          },
        }
        const groupedDataKeys = ['initial value', 'positive', 'negative', 'total']
        const newConfig = {
          ItemsHelper: {
            ...oldConfig.ItemsHelper,
            colorDict: [...groupedDataKeys].reduce((accumulator, current, i) => {
              accumulator[current] = {
                ...colors[current],
                color: colors[current]?.color || getColor({ type: current, id: current }),
                pattern: getPatternByIndex(i),
              }
              return accumulator
            }, {}),
          },
        }
        return {
          oldConfig,
          newConfig,
        }
      },
    },
  }
}
